import { Alert, Box, Typography } from '@mui/material';
import styled from 'styled-components';

import Scrollbar from '../Scrollbar';

interface LoadingContainerProps {
  decorate: boolean;
}

const LoadingContainer = styled.div<LoadingContainerProps>`
  /* fallback for browsers without dvh support */
  height: ${({ decorate, theme }) =>
    decorate ? `calc(100vh - ${theme.shape.appbarHeight * 2}px)` : '100vh'};
  height: ${({ decorate, theme }) =>
    decorate ? `calc(100dvh - ${theme.shape.appbarHeight * 2}px)` : '100dvh'};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface MainProps {
  $isSm: boolean;
  $expanded: boolean;
  $hideAppbar: boolean;
  $hidePadding: boolean;
}

const Main = styled(Box)<MainProps>`
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  /* In drawer mode the inner panel scrolls; in hideDrawer mode Main scrolls. */
  overflow-y: ${({ $hideAppbar }) => ($hideAppbar ? 'auto' : 'hidden')};
  margin-left: ${({ $expanded, theme, $isSm, $hideAppbar }) => {
    if ($hideAppbar || !$isSm) {
      return 0;
    }
    return `${$expanded ? theme.shape.drawerWidth : theme.shape.drawerWidthRail}px`;
  }};
  transition: ${({ theme }) =>
    theme.transitions.create('margin-left', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    })};
  padding: ${({ theme, $isSm, $hideAppbar, $hidePadding }) => {
    if (!$hideAppbar) {
      return 0;
    }
    if ($hidePadding) {
      return 0;
    }
    return theme.spacing($isSm ? 4 : 2);
  }};
`;

// Tablet/desktop: an overlay scrollbar (zero layout width) floats above the
// content, so it never reflows the page and the content stays full-width and
// centred with no track reserved on the right. Overflow is driven by the
// `Scrollbar` options, not CSS here.
const ContentScroll = styled(Scrollbar)`
  flex: 1 1 auto;
  min-height: 0;
`;

// Mobile: fall back to the native scrollbar, which mobile browsers already render
// as a thin, auto-hiding overlay that reserves no space.
const ContentScrollNative = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

interface ContentPadProps {
  $isSm: boolean;
  $hidePadding: boolean;
}

// A scrollport is a padding box, so content scrolls through the gutter above it.
// `GutterCap` covers that band and `StickyToolbar` pins below it.
const ContentPad = styled.div<ContentPadProps>`
  --page-gutter: ${({ theme, $isSm, $hidePadding }) =>
    theme.spacing($hidePadding ? 0 : $isSm ? 4 : 2)};
  padding: var(--page-gutter);
`;

interface GutterCapProps {
  $painted: boolean;
}

/**
 * Covers the top gutter, so nothing shows above a toolbar pinned below it. It
 * paints only while the page is scrolled, because a tab badge can overflow into
 * the band at rest — and it takes the clicks in the band only then too, the rows
 * beneath it being hidden. Negative margins keep it out of the page's flow.
 */
const GutterCap = styled.div<GutterCapProps>`
  position: sticky;
  top: 0;
  height: var(--page-gutter);
  margin: calc(-1 * var(--page-gutter)) calc(-1 * var(--page-gutter)) 0;
  background-color: ${({ theme, $painted }) =>
    $painted ? theme.palette.background.canvas : 'transparent'};
  pointer-events: ${({ $painted }) => ($painted ? 'auto' : 'none')};
  z-index: ${({ theme }) => theme.zIndex.appBar};
`;

const ContentContainer = styled.div`
  display: flex;
  /* fallback for browsers without dvh support */
  height: 100vh;
  /* dvh tracks the browser's dynamic toolbars so the bottom nav isn't hidden
     behind mobile chrome (e.g. Chrome's bottom controls bar). */
  height: 100dvh;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const Nav = styled(Box)``;

const SnackbarAlert = styled(Alert)`
  && {
    width: 100%;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  width: 100%;
  /* fallback for browsers without dvh support */
  height: 80vh;
  height: 80dvh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const IconContainer = styled.div`
  color: ${({ theme }) => theme.palette.primary.main};
  @keyframes bounce {
    0%,
    20%,
    50%,
    70%,
    90%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-12px);
    }
    60% {
      transform: translateY(-8px);
    }
    80% {
      transform: translateY(-5px);
    }
  }
  animation: bounce 1.5s ease;
  display: flex;
`;

const IconBorder = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.palette.primary.light}80;
  border-style: dotted;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageTitle = styled(Typography)`
  && {
    margin-top: ${({ theme }) => theme.spacing(2)};
    margin-bottom: ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const MessageContent = styled(Typography)`
  && {
    text-align: center;
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }
`;

export {
  ContentContainer,
  ContentPad,
  ContentScroll,
  ContentScrollNative,
  GutterCap,
  IconBorder,
  IconContainer,
  LoadingContainer,
  Main,
  MessageContainer,
  MessageContent,
  MessageTitle,
  Nav,
  SnackbarAlert,
};
